// 列ID・列名管理のユーティリティ関数

import type { DataSet, ColumnSchema, ColumnId, ColumnName, DataValue } from '../../types';
import { createColumnId } from '../../types';
import logger from '../utils/logger';

/**
 * 列名から列IDを取得する
 */
export function getColumnIdByName(dataset: DataSet, columnName: ColumnName): ColumnId | null {
  const column = dataset.schema.columns.find((col: ColumnSchema) => col.name === columnName);
  return column?.id || null;
}

/**
 * 列IDから列スキーマを取得する
 */
export function getColumnById(dataset: DataSet, columnId: ColumnId): ColumnSchema | null {
  return dataset.schema.columns.find((col: ColumnSchema) => col.id === columnId) || null;
}

/**
 * 列IDから列名を取得する
 */
export function getColumnNameById(dataset: DataSet, columnId: ColumnId): ColumnName | null {
  const column = getColumnById(dataset, columnId);
  return column?.name || null;
}

/**
 * 列IDから列データを取得する
 */
export function getColumnData(dataset: DataSet, columnId: ColumnId): DataValue[] | null {
  if (dataset.type === 'primary') {
    return dataset.data[columnId] || null;
  } else if (dataset.type === 'derived') {
    const derivedDataset = dataset as import('../../types').DerivedDataSet;
    if (!derivedDataset.data) {
      logger.warn('DerivedDataSet has no data available');
      return null;
    }
    return derivedDataset.data[columnId] || null;
  } else if (dataset.type === 'ephemeral') {
    const ephemeralDataset = dataset as import('../../types').EphemeralDataSet;
    return ephemeralDataset.data[columnId] || null;
  }
  return null;
}

/**
 * 列名から列データを取得する
 */
export function getColumnDataByName(dataset: DataSet, columnName: ColumnName): DataValue[] | null {
  const columnId = getColumnIdByName(dataset, columnName);
  return columnId ? getColumnData(dataset, columnId) : null;
}

/**
 * 列名を変更する
 */
export function renameColumn(dataset: DataSet, columnId: ColumnId, newName: ColumnName): DataSet {
  const updatedColumns = dataset.schema.columns.map((col: ColumnSchema) => 
    col.id === columnId ? { ...col, name: newName } : col
  );
  
  return {
    ...dataset,
    schema: {
      ...dataset.schema,
      columns: updatedColumns
    }
  };
}

/**
 * 一意な列IDを生成する
 */
export function generateColumnId(baseName: string): ColumnId {
  // タイムスタンプとランダム文字列を組み合わせて一意性を保証
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).substring(2, 8);
  const cleanBaseName = baseName.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
  return createColumnId(`col_${cleanBaseName}_${timestamp}_${random}`);
}

/**
 * データセットから列のデータを安全に取得する（ID受け取り）
 * DerivedDataSetの場合も考慮
 */
export function getColumnDataSafe(dataset: DataSet, columnId: ColumnId): DataValue[] | null {
  if (dataset.type === 'primary' && columnId in dataset.data) {
    return dataset.data[columnId];
  } else if (dataset.type === 'derived' && dataset.data) {
    // DerivedDataSetの場合、dataが{ [key: string]: DataValue[] }の可能性がある
    const data = dataset.data as Record<ColumnId, DataValue[]>;
    return data[columnId] || null;
  } else if (dataset.type === 'ephemeral' && columnId in dataset.data) {
    return dataset.data[columnId];
  }
  return null;
}


